import { html, LitElement, nothing, TemplateResult } from 'lit';
import { NetworkTreeSearchController } from '../controller/NetworkTreeSearchController';
import { customElement, property } from 'lit/decorators.js';
import { WEB_COMPONENT_NETWORK_TREE_SEARCH } from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import { map } from 'lit/directives/map.js';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { emphasizeFirstOccurrence } from '../../../utils/htmlElement.utils';
import { htmlEncode } from '../../../utils/escape.utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { TreeSearchEvent } from '../types';
import { ButtonColor } from '../../../ui/gzIconButton/types';
import { styles } from '../view/styles';
import { GzListItemWebComponent } from '../../../ui/gzListItem';
import { debounce } from '../../../utils/event.utils';
import { IconProperties } from '../../../ui/gzIcon/types';
import { IconLocation } from '../../networkAppContainer/helpers/types';
import { NetworkIconHelper } from '../../networkAppContainer/helpers/icons/network.icon.helper';

@customElement(WEB_COMPONENT_NETWORK_TREE_SEARCH)
export class GzNetworkTreeSearchWebComponent extends LitElement {
  private controller = new NetworkTreeSearchController(this);
  private searchString: string;
  private lastActiveItem: GzListItemWebComponent | undefined = undefined;

  @property({ attribute: true, reflect: true, type: Boolean })
  isPartner: boolean;

  static get styles() {
    return styles;
  }

  protected render(): TemplateResult {
    return html`
      <div class="search-header">
        <gz-input-search
          placeholder="${translateFn('Search in tree view')}"
          @change="${debounce(this.handleInputChange, 500)}"
        >
        </gz-input-search>
      </div>
      ${this.renderSearchResult()}
    `;
  }

  private renderSearchResult(): TemplateResult {
    return this.controller.isLoading
      ? html`<gz-loader></gz-loader>`
      : html`
          <div class="tree-search-result" @scroll="${this.handleScroll}">
            ${this.controller.searchResult.length !== 0
              ? map(this.controller.searchResult, item => this.renderSearchItem(item))
              : this.renderNoItemsFound()}
          </div>
        `;
  }

  private async handleScroll() {
    await this.controller.loadMoreResults();
  }

  private renderNoItemsFound(): TemplateResult {
    if (!this.searchString || this.searchString === '') {
      return html`${nothing}`;
    }
    return html`<span class="tree-no-search-result"
      >${this.isPartner ? translateFn('No folder or company found') : translateFn('No folder found')}</span
    >`;
  }

  private renderSearchItem(item: ProtectedEntity): TemplateResult {
    const textToBeEmphasize = this.isPartner ? item.name + ' (' + item.company?.name + ')' : item.name;
    const iconProperties: IconProperties = NetworkIconHelper.getIconForProtectedEntity(item, IconLocation.TREE);

    return html`
      <div class="network-search-item">
        <gz-icon .name="${iconProperties.icon}" .color="${ButtonColor.SECONDARY}"></gz-icon>
        <gz-list-item @itemClick="${this.handleSearchItemClicked}" .itemData="${item}">
          ${unsafeHTML(emphasizeFirstOccurrence(this.searchString, htmlEncode(textToBeEmphasize)))}
        </gz-list-item>
      </div>
    `;
  }

  private handleSearchItemClicked(event: CustomEvent) {
    const data = event.detail.itemData as ProtectedEntity;
    this.dispatchEvent(
      new CustomEvent(TreeSearchEvent.ITEM_CLICKED, {
        bubbles: true,
        composed: true,
        detail: { protectedEntity: data },
      })
    );
    if (this.lastActiveItem) {
      (this.lastActiveItem as GzListItemWebComponent).active = false;
    }

    const currentActiveItem = event.currentTarget as GzListItemWebComponent;
    currentActiveItem.active = true;
    this.lastActiveItem = currentActiveItem;
  }

  private async handleInputChange(event: CustomEvent) {
    this.searchString = event.detail.sourceEvent;

    if (this.searchString === '') {
      const shouldReconstructTree = this.lastActiveItem !== undefined;
      this.dispatchEvent(
        new CustomEvent(TreeSearchEvent.INPUT_RESET, {
          bubbles: true,
          composed: true,
          detail: { shouldReconstructTree },
        })
      );
      this.controller.searchResult = [];
      this.requestUpdate();
      this.lastActiveItem = undefined;
    }

    await this.controller.search(this.searchString);
  }
}
